import styled from "styled-components"
import { Link } from "react-router-dom"
import useTranslation from "../../hooks/useTranslation"
import resolve from "../../../../common/utils/object/resolve"
import fillPath from "./fillPath"

/*
 * A drill-through from a record to the document it came from.
 *
 * A posted journal is the case it was written for. The ledger knows exactly
 * where a posting came from — `source` carries the kind, the id and the number —
 * and until now a reader could see the description and had no way to open the
 * invoice behind it. The route is not derivable from the data on its own,
 * because each kind of source document lives in a different module, so the
 * config supplies a template per kind and this fills it in.
 *
 * ALWAYS A MAP, even for the one destination a journal's reversal link has. A
 * second `path`/`label` pair for the fixed case would be two ways to say one
 * thing, and `value` — the field the map is keyed by — is also what keys the
 * element in its array (lint:arrayKeys) and in React's reconciler, so an element
 * without one is not a shortcut, it is a missing key.
 *
 * Not `DisplayElement`'s `link`/`url`, which open an external address in a new
 * tab. This one navigates inside the app, so it is a router `Link`: the back
 * button works, and so does opening it in a new tab from the context menu.
 *
 * WHAT IT DOES WHEN IT CANNOT LINK, in the order the cases are decided:
 *
 *   the path fills          → the link
 *   `textField` has a value → that text, unlinked
 *   neither                 → nothing
 *
 * Both fallbacks are ordinary rather than edge cases. A journal posted before
 * its ruleset recorded a navigable id has a `source` that names its document and
 * cannot locate it — the number is still worth reading. A kind the config has no
 * route for has nothing at all to offer: a manual journal's source is itself,
 * and a label promising a link that does not link is worse than silence.
 *
 * This is also why no config here carries a `condition`. The element's own
 * visibility predicate cannot express any of it: `field` in the shared condition
 * language is a FLAT key, so `field: 'source.type'` reads `values['source.type']`
 * and is quietly always false. Deciding it here is not only simpler, it is the
 * only place the decision can be made correctly.
 */

const Wrapper = styled.span`
    display: inline-flex;
    align-items: baseline;
    gap: 0.35rem;
`

const Label = styled.span`
    color: ${({ theme }) => theme.font.label};
`

const Anchor = styled(Link)`
    color: ${({ theme }) => theme.emotive.info.color};
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`

const DisplayLink = ({ displayData, element }) => {
    const { t } = useTranslation()

    // `value` names the field both maps are keyed by — a journal's source.type,
    // or its state for the link to the journal that reversed it.
    const selector = resolve(element.value, displayData)
    const template = element.pathMap?.[selector]
    const labelKey = element.labelMap?.[selector]

    const label = labelKey ? t(labelKey) : null
    const text = element.textField ? resolve(element.textField, displayData) : null
    const to = template ? fillPath(template, displayData) : null

    // A label with nowhere to go and nothing to state is a promise the element
    // cannot keep — every journal whose source is itself lands here.
    if (!to && !text) return null
    if (!label && !text) return null

    // The label carries the meaning when there is no number to show ("Open the
    // journal that reversed this"), the number when there is ("Invoice ·
    // FK-1001").
    const body = text ? (label ? `${label} · ${text}` : text) : label

    /*
     * Named after the value it resolved for — 'docLink.PAYMENT', 'docLink.reversed'
     * — so a spec can click the link and assert where it lands. Nothing else in a
     * display carries a test id, and nothing else in one navigates either: the
     * only way to prove a drill-through works is to take it.
     *
     * On the unlinked branch too, deliberately. A spec then fails differently
     * depending on what went wrong — element missing, or element there and not a
     * link — instead of "could not find it" for both.
     */
    const testId = `docLink.${selector}`

    // No icon here: DisplayElementField already draws `element.icon` ahead of
    // every element type, this one included.
    return (
        <Wrapper>
            {to
                ? <Anchor to={to} data-testid={testId} onClick={e => e.stopPropagation()}>{body}</Anchor>
                : <Label data-testid={testId}>{body}</Label>}
        </Wrapper>
    )
}

export default DisplayLink
